import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Copy, Download, Layers, MoreHorizontal, PencilLine, Share2, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DeleteDialog } from "@/components/delete-dialog";
import { ShareDialog } from "@/components/shares/share-dialog";
import { FolderNameDialog } from "@/components/projects/folder-name-dialog";
import {
  fetchVideoDownloadUrl,
  triggerDownload,
  useDeleteVideo,
  useDeleteVideoPermanent,
  useDuplicateVideo,
  useRenameVideo,
} from "@/hooks/use-videos";
import type { Video } from "@/lib/api-types";

type Props = {
  projectId: string;
  video: Video;
  className?: string;
  /**
   * "version" is used on the per-version cards inside the versions page, where
   * "Upload new version" (you're already inside a version) and "Rename" (a
   * version inherits the stack title) don't apply and are hidden. Everything
   * else — Share, Download, Duplicate, Delete — stays.
   */
  context?: "asset" | "version";
};

/**
 * Menu items:
 *   Share…      — opens the new-model ShareDialog with this video preselected
 *                 (post-migration 0013). Supersedes the old "Share link" quick-
 *                 copy path — that used POST /videos/:id/share which is now
 *                 legacy. Task 14 removes the legacy hook.
 *   Download    — fetches a presigned URL from GET /videos/:id/download and
 *                 triggers a browser save (2026-07-18). The old direct
 *                 <a href={cdnUrl} download> broke on cross-origin (lab.
 *                 imgwala.com → cdn.lab.imgwala.com) — browsers ignore the
 *                 `download` attribute cross-origin and just open the file
 *                 in a new tab. The presigned URL carries a signed
 *                 response-content-disposition=attachment so the CDN response
 *                 forces a save regardless of origin.
 *   Duplicate   — POST /api/editor/videos/:id/duplicate (2026-07-18).
 *                 Server-side R2 copy + fresh V1 row. Menu item shows a
 *                 "Duplicating…" state while the mutation is in flight —
 *                 large files can take several seconds. Hard cap at
 *                 DUPLICATE_MAX_BYTES (200 MB) — sources above that grey
 *                 out the item with a "too large" tooltip. Backend belt+
 *                 braces returns 413 with the same message.
 *   Rename      — opens FolderNameDialog with noun="asset" (2026-07-18).
 *                 Backend: PATCH /api/editor/videos/:id { title }.
 *   Delete      — DeleteDialog with two branches (Move to Trash / Delete Permanently).
 */
// Must stay in sync with DUPLICATE_MAX_BYTES in worker/routes/editor.js.
// Backend is authoritative; this is only for greying the menu item so users
// don't waste a click on a doomed request.
const DUPLICATE_MAX_BYTES = 200 * 1024 * 1024;

export function VideoCardMenu({ projectId, video, className, context = "asset" }: Props) {
  const isVersion = context === "version";
  const deleteVideo = useDeleteVideo(projectId);
  const permanentDeleteVideo = useDeleteVideoPermanent(projectId);
  const renameVideo = useRenameVideo(projectId);
  const duplicateVideo = useDuplicateVideo(projectId);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();
  // Upload page lives under the same role prefix as the current view.
  const uploadBase = loc.pathname.startsWith("/admin") ? "/admin/upload" : "/app/upload";

  const tooLargeToDuplicate = (video.fileSizeBytes ?? 0) > DUPLICATE_MAX_BYTES;

  return (
    <div className={className} onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Asset options"
            className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          >
            <MoreHorizontal className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onSelect={() => setShareOpen(true)}>
            <Share2 />
            Share…
          </DropdownMenuItem>

          <DropdownMenuItem
            onSelect={async (e) => {
              e.preventDefault();
              try {
                const { downloadUrl, filename } = await fetchVideoDownloadUrl(video.id);
                triggerDownload(downloadUrl, filename);
              } catch {
                /* silent — see selection bar for the same rationale */
              }
            }}
          >
            <Download />
            Download
          </DropdownMenuItem>

          <DropdownMenuItem
            onSelect={(e) => {
              // Prevent Radix from auto-closing before the mutation resolves —
              // otherwise the pending state disappears the instant the user
              // clicks, which is confusing on multi-second R2 copies.
              e.preventDefault();
              if (tooLargeToDuplicate) return;
              duplicateVideo.mutate(video.id);
            }}
            disabled={duplicateVideo.isPending || tooLargeToDuplicate}
            title={
              tooLargeToDuplicate
                ? `File too large to duplicate (over ${Math.round(DUPLICATE_MAX_BYTES / (1024 * 1024))} MB)`
                : undefined
            }
          >
            <Copy />
            {duplicateVideo.isPending
              ? "Duplicating…"
              : tooLargeToDuplicate
                ? "Duplicate (file too large)"
                : "Duplicate"}
          </DropdownMenuItem>

          {!isVersion && (
            <DropdownMenuItem onSelect={() => setRenameOpen(true)}>
              <PencilLine />
              Rename
            </DropdownMenuItem>
          )}

          {!isVersion && (
            <DropdownMenuItem
              onSelect={() =>
                navigate(`${uploadBase}?projectId=${projectId}&versionOf=${video.id}`)
              }
            >
              <Layers />
              Upload new version
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          <DropdownMenuItem
            onSelect={() => setDeleteOpen(true)}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        items={[{ type: "video", id: video.id, name: video.title }]}
      />

      <FolderNameDialog
        mode="rename"
        noun="asset"
        initialName={video.title}
        open={renameOpen}
        onOpenChange={setRenameOpen}
        onSubmit={(title) =>
          renameVideo.mutateAsync({ videoId: video.id, title }).then(() => undefined)
        }
      />

      <DeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        itemLabel="asset"
        itemName={video.title}
        onTrash={() => deleteVideo.mutateAsync(video.id).then(() => undefined)}
        onPermanent={() =>
          permanentDeleteVideo.mutateAsync(video.id).then(() => undefined)
        }
      />
    </div>
  );
}
